import {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {Link} from 'react-router-dom'
import {
    FileText,
    MoreHorizontal,
    Trash,
    Eye
} from 'react-feather'
import DataTable from 'react-data-table-component'
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Button,
    DropdownToggle,
    UncontrolledDropdown,
    DropdownMenu,
    DropdownItem,
    CustomInput,
    Spinner
} from 'reactstrap'
import CustomPagination from '@src/views/components/pagination/CustomPagination'
import {
    adminGetRds,
    adminDeleteRdStatus,
    adminDeleteRdStatusReset,
    adminExportRds,
    adminApproveRd,
    adminApproveRdReset
} from "@src/redux/actions/AdminActions"
import AdminFilters from "@src/views/components/AdminFilters"
import queryString from "query-string"
import {confirmAlert, successAlert} from "../../components/CustomAlerts"

const PendingRDs = () => {
    const dispatch = useDispatch()

    const [filtersData, setFiltersData] = useState(null)

    const {
        adminGetRDsLoading,
        adminGetRDsData,
        adminGetRDsError,

        adminDeleteRDLoading,
        adminDeleteRDData,
        adminDeleteRDError,

        adminRDExportLoading,
        adminRDExportData,
        adminRDExportError,

        adminApproveRDLoading,
        adminApproveRDData,
        adminApproveRDError
    } = useSelector(store => store.adminReducer)

    useEffect(() => {
        if (adminDeleteRDData?.success) {
            successAlert(adminDeleteRDData?.message)
            dispatch(adminDeleteRdStatusReset())
            dispatch(adminGetRds({profile_status: 'pending'}))
        }

        if (adminApproveRDData?.status) {
            successAlert(adminApproveRDData?.msg)
            dispatch(adminApproveRdReset())
            dispatch(adminGetRds({profile_status: 'pending'}))
        }
    }, [
        adminDeleteRDData,
        adminApproveRDData
    ])

    useEffect(() => {
        dispatch(adminGetRds({
            profile_status: 'pending'
        }))
        dispatch(adminExportRds('?data-type=export&profile_status=pending'))
    }, [])

    const searchData = (data) => {
        setFiltersData(data)
        const {
            name, email, country, region, state, city, industry, speciality, status
        } = data
        dispatch(adminGetRds({
            profile_status: 'pending',
            name: name,
            email: email,
            country: country ? country.id : '',
            region: region ? region.id : '',
            state: state ? state.id : '',
            city: city ? city.id : '',
            industry: industry ? [industry.id] : [],
            speciality_id: speciality ? speciality.id : '',
            status: status ? status.value : ''
        }))
    }

    const convertArrayOfObjectsToCSV = (array) => {
        let result = ''
        result += "Name, Email, CDR Number, Proof of Certification, Country, Region, States, Cities, Industry, Speciality , Status, Reg Date, Profile Status, Profile Image, Location, Timezone \n"
        array.forEach(item => {
            result += `${item?.name?.replaceAll(',', '')}, ${item?.email}, ${item?.cdr_number ? item.cdr_number.replaceAll('#', 'No.') : ''}, ${item?.certification || ''}, ${item?.country}, ${item?.region}, ${item?.state}, ${item?.city},`
            result += `${item?.industry.map(e => e.label).join(" | ").replaceAll(',', ' ')},`
            result += `${item?.speciality.map(e => e.label).join(" | ").replaceAll(',', ' ')},`
            result += `${item?.status}, ${item?.reg_date.replaceAll(',', ' ')}, ${item?.profile_status}, ${item?.profile_image}, ${item?.location}, ${item?.timezone}`
            result += "\n"
        })
        return result
    }

    const downloadCSV = (array) => {
        if (array.length > 0) {
            const link = document.createElement('a')
            let csv = convertArrayOfObjectsToCSV(array)
            if (csv === null) return
            const filename = 'Pending-RDS.csv'
            if (!csv.match(/^data:text\/csv/i)) {
                csv = `data:text/csv;charset=utf-8,${csv}`
            }
            link.setAttribute('href', encodeURI(csv))
            link.setAttribute('download', filename)
            link.click()
        } else {
            alert('Data is being loaded, Please wait...')
        }
    }

    const handleCustomPagination = (pageURL) => {
        const parsed = queryString.parseUrl(pageURL);
        let obj = {
            page: parsed?.query?.page,
            profile_status: 'pending'
        }
        if (filtersData) {
            const {
                name, email, country, region, state, city, industry, speciality, status
            } = filtersData
            obj = {
                ...obj,
                name: name,
                email: email,
                country: country ? country.id : '',
                region: region ? region.id : '',
                state: state ? state.id : '',
                city: city ? city.id : '',
                industry: industry ? [industry.id] : [],
                speciality_id: speciality ? speciality.id : '',
                status: status ? status.value : ''
            }
        }
        dispatch(adminGetRds(obj))
    }

    const deleteRd = async (id) => {
        const result = await confirmAlert('Confirm', 'Are you sure you want to delete this RD?')
        if (result) {
            dispatch(adminDeleteRdStatus(id))
        }
    }

    const approveRD = async (id) => {
        const result = await confirmAlert('Confirm', 'Are you sure you want to approve this RD?')
        if (result) {
            dispatch(adminApproveRd({id}))
        }
    }

    const advSearchColumns = [
        {
            name: 'Name',
            selector: 'name',
            sortable: true,
            wrap: true,
            cell: row => {
                return (
                    <Link to={`/admin/manage-rd/${row?.id}`} className='font-weight-bold'>{row?.name}</Link>
                )
            }
        },
        {
            name: 'Email',
            selector: 'email',
            sortable: true,
            wrap: true
        },
        {
            name: 'CDR / Proof',
            selector: 'cdr_number',
            sortable: true,
            wrap: true,
            cell: row => {
                return (
                    <div className='d-flex flex-column py-50'>
                        {row?.cdr_number ? <span>{row.cdr_number}</span> : null}
                        {row?.certification ? (
                            <a
                                href={row.certification}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='badge badge-light-primary text-primary d-inline-flex align-items-center mt-25'
                                style={{ textDecoration: 'none', width: 'fit-content' }}
                            >
                                <FileText size={12} className='mr-25' /> View Proof
                            </a>
                        ) : null}
                        {!row?.cdr_number && !row?.certification ? <span>-</span> : null}
                    </div>
                )
            }
        },
        {
            name: 'State',
            selector: 'state',
            sortable: true,
            wrap: true
        },
        {
            name: 'Industry',
            selector: 'industry',
            sortable: true,
            wrap: true,
            cell: row => {
                return (
                    <div>
                        {row?.industry?.map((item, index) => (
                            <p key={`rd-industry-${index}`} className='mb-0'>
                                {item?.label}{index !== row?.industry?.length - 1 && ','}
                            </p>
                        ))}
                    </div>
                )
            }
        },
        {
            name: 'Speciality',
            selector: 'speciality',
            sortable: true,
            wrap: true,
            cell: row => {
                return (
                    <div>
                        {row.speciality?.map((item, index) => (
                            <p key={`rd-speciality-${index}`} className='mb-0'>
                                {item?.label}{index !== row?.speciality?.length - 1 && ','}
                            </p>
                        ))}
                    </div>
                )
            }
        },
        {
            name: 'Registration Date',
            selector: 'reg_date',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Approval',
            selector: 'status',
            sortable: true,
            wrap: true,
            cell: row => {
                return (
                    <CustomInput className='custom-control-success'
                                 type='switch'
                                 id={`success_${row.id}`}
                                 name='success'
                                 inline={true}
                                 checked={false}
                                 onClick={() => {
                                     approveRD(row.id).then(r => r)
                                 }}
                    />
                )
            }
        },
        {
            name: 'Actions',
            allowOverflow: true,
            cell: row => {
                return (
                    <div className='d-flex pl-2 cursor-pointer'>
                        <UncontrolledDropdown>
                            <DropdownToggle className='pr-1' tag='span'>
                                <MoreHorizontal size={15}/>
                            </DropdownToggle>
                            <DropdownMenu right>
                                <DropdownItem tag={Link} to={`/admin/manage-rd-pending/${row.id}`}>
                                    <Eye size={15}/>
                                    <span className='align-middle ml-50'>View Profile</span>
                                </DropdownItem>
                                <DropdownItem className='text-danger' onClick={() => {
                                    deleteRd(row.id).then(r => r)
                                }}>
                                    <Trash size={15}/>
                                    <span className='align-middle ml-50'>Delete</span>
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </div>
                )
            }
        }
    ]

    return (
        <Card className="brands-my-campaign-table-parent">
            <CardHeader className='d-flex align-items-center justify-content-between flex-wrap border-bottom p-1 px-2'>
                <CardTitle className="my-1" tag='h4'>Pending RDs</CardTitle>
                <Button.Ripple className="my-1"
                               color='primary'
                               onClick={() => downloadCSV(adminRDExportData?.data)}
                               disabled={adminRDExportLoading}
                >
                    <FileText size={15}/> Export (CSV)
                </Button.Ripple>
            </CardHeader>
            <CardBody>
                <AdminFilters
                    onSubmit={(data) => searchData(data)}
                    onClear={() => {
                        dispatch(adminGetRds({profile_status: 'pending'}))
                        setFiltersData(null)
                    }}
                />
                {adminGetRDsLoading ? <div className="w-100 my-4 text-center"><Spinner color='primary'/></div> :
                    <DataTable noHeader={true}
                               columns={advSearchColumns}
                               className='react-dataTable'
                               data={adminGetRDsData?.data}
                    />
                }
                <CustomPagination
                    pageLinks={adminGetRDsData?.pages}
                    onPaginationClick={handleCustomPagination}
                />
            </CardBody>
        </Card>
    )
}

export default PendingRDs
